/**
 * After each assistant turn, ask Claude to distill the exchange into a
 * single short learning that can be stored in the memory rolling log.
 *
 * Matches the native distillation prompt conceptually — the native
 * summary is "10-15 words max, a fact Koko learned" per
 * `KokoMemoryManager.swift:82`.
 *
 * Uses the existing `ClaudeClient` (no streaming needed — we just want
 * the final text) with a tiny system prompt. Fired in the background
 * after a turn completes; failures are silent.
 */

import type { ClaudeClient } from "../claude/ClaudeClient";
import type { PageContext } from "../types";

const DISTILLATION_PROMPT =
  "you're koko's memory archivist. given the user's question and your reply, " +
  "write a single short line — 10 to 15 words max — that captures the ONE fact " +
  "worth remembering for next time. all lowercase. no punctuation beyond commas. " +
  "no meta-commentary like 'user asked about' — just the fact itself. if there's " +
  "nothing worth remembering (small talk, trivial question), reply with only the " +
  "word: skip";

interface BlankContext {
  domSummary: string;
  screenshotBase64: string;
  screenshotMediaType: string;
  viewportWidth: number;
  viewportHeight: number;
}

/**
 * Minimal page context the summarizer call needs. We reuse the same
 * screenshot from the original turn rather than rasterizing again.
 */
function buildMinimalPageContext(
  originalContext: PageContext | null,
): BlankContext {
  if (originalContext) return originalContext;
  return {
    domSummary: "",
    // 1x1 transparent PNG fallback — satisfies the image requirement if
    // we somehow have no real context to carry forward.
    screenshotBase64:
      "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=",
    screenshotMediaType: "image/png",
    viewportWidth: 1,
    viewportHeight: 1,
  };
}

export async function summarizeTurnInBackground(
  claudeClient: ClaudeClient,
  userText: string,
  assistantText: string,
  pageContext: PageContext | null,
): Promise<string | null> {
  const minimalContext = buildMinimalPageContext(pageContext);

  const userPromptForSummary =
    `User said: ${userText}\n\n` +
    `Koko replied: ${assistantText}\n\n` +
    `What's the one fact worth remembering?`;

  let accumulated = "";
  await claudeClient.streamResponse(
    {
      systemPrompt: DISTILLATION_PROMPT,
      history: [],
      userText: userPromptForSummary,
      pageContext: minimalContext,
      // Use haiku-class defaults — the Worker will still accept the
      // sonnet default if our env doesn't wire an alternate model.
    },
    {
      onTextChunk: (chunk) => {
        accumulated += chunk;
      },
      onComplete: () => {},
      onError: () => {},
    },
  );

  const cleanSummary = accumulated.trim().replace(/[\r\n]+/g, " ");
  if (cleanSummary.length === 0) return null;
  if (cleanSummary.toLowerCase() === "skip") return null;
  return cleanSummary;
}
